import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Ban } from "lucide-react";
import { format } from "date-fns";

type JobRow = {
  id: string;
  job_type: string;
  status: string;
  attempts: number;
  max_attempts: number;
  next_run_at: string | null;
  updated_at: string;
  last_error: string | null;
};

const STATUS_STYLE: Record<string, string> = {
  queued: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  running: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  failed: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  done: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  cancelled: "bg-muted text-muted-foreground",
};

function fmt(d?: string | null) {
  if (!d) return "—";
  return format(new Date(d), "dd/MM/yyyy HH:mm");
}

export default function JobsPanel() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"active" | "failed" | "all">("active");

  // Summary counts (cheap aggregate via small head/count queries per group is heavy;
  // grab a window of recent rows and aggregate client-side).
  const { data: summary, isLoading: sumLoading } = useQuery({
    queryKey: ["research_ingest_jobs_summary"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_ingest_jobs")
        .select("job_type,status")
        .in("status", ["queued", "running", "failed"])
        .limit(10000);
      if (error) throw error;
      return data as { job_type: string; status: string }[];
    },
    refetchInterval: 15000,
  });

  // Recent runs / stuck running / failures
  const { data: rows, isLoading } = useQuery({
    queryKey: ["research_ingest_jobs_rows", filter],
    queryFn: async () => {
      let q = supabase
        .from("research_ingest_jobs")
        .select("id, job_type, status, attempts, max_attempts, next_run_at, updated_at, last_error")
        .order("updated_at", { ascending: false })
        .limit(200);
      if (filter === "active") q = q.in("status", ["queued", "running"]);
      if (filter === "failed") q = q.eq("status", "failed");
      const { data, error } = await q;
      if (error) throw error;
      return data as JobRow[];
    },
    refetchInterval: 15000,
  });

  // Recent AI runs to spot cost spikes / in-flight calls
  const { data: aiRuns } = useQuery({
    queryKey: ["research_ai_runs_recent"],
    queryFn: async () => {
      const since = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("research_ai_call_runs")
        .select("id, action, model, status, cost_usd, latency_ms, created_at, completed_at")
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as any[];
    },
    refetchInterval: 15000,
  });

  type Grouped = { job_type: string; queued: number; running: number; failed: number };
  const grouped = useMemo<Grouped[]>(() => {
    const m = new Map<string, Grouped>();
    for (const r of summary ?? []) {
      const row = m.get(r.job_type) ?? { job_type: r.job_type, queued: 0, running: 0, failed: 0 };
      if (r.status === "queued" || r.status === "running" || r.status === "failed") {
        row[r.status] += 1;
      }
      m.set(r.job_type, row);
    }
    return [...m.values()].sort(
      (a, b) => (b.queued + b.running + b.failed) - (a.queued + a.running + a.failed)
    );
  }, [summary]);

  const totals = useMemo(() => {
    const t = { queued: 0, running: 0, failed: 0 };
    for (const g of grouped) {
      t.queued += g.queued;
      t.running += g.running;
      t.failed += g.failed;
    }
    return t;
  }, [grouped]);

  const inflightCost = (aiRuns ?? []).reduce((s, r) => s + Number(r.cost_usd ?? 0), 0);
  const inflightCount = (aiRuns ?? []).filter((r) => !r.completed_at).length;

  const cancelGroup = useMutation({
    mutationFn: async (job_type: string) => {
      const { error } = await supabase
        .from("research_ingest_jobs")
        .update({ status: "cancelled", updated_at: new Date().toISOString() })
        .eq("job_type", job_type)
        .eq("status", "queued");
      if (error) throw error;
    },
    onSuccess: (_d, jt) => {
      toast.success(`Cancelled queued ${jt} jobs`);
      qc.invalidateQueries({ queryKey: ["research_ingest_jobs_summary"] });
      qc.invalidateQueries({ queryKey: ["research_ingest_jobs_rows"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const retryFailed = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("research_ingest_jobs")
        .update({ status: "queued", attempts: 0, next_run_at: new Date().toISOString(), last_error: null, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Re-queued");
      qc.invalidateQueries({ queryKey: ["research_ingest_jobs_summary"] });
      qc.invalidateQueries({ queryKey: ["research_ingest_jobs_rows"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const cancelOne = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("research_ingest_jobs")
        .update({ status: "cancelled", updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["research_ingest_jobs_summary"] });
      qc.invalidateQueries({ queryKey: ["research_ingest_jobs_rows"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Stuck = running and not updated in last 30 minutes
  const stuckThreshold = Date.now() - 30 * 60 * 1000;
  const stuck = (rows ?? []).filter((r) => r.status === "running" && new Date(r.updated_at).getTime() < stuckThreshold);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Jobs & in-flight AI calls</h2>
        <div className="text-xs text-muted-foreground flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Auto-refreshing every 15s
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Queued" value={totals.queued.toLocaleString()} tone={totals.queued > 1000 ? "warn" : "default"} />
        <Stat label="Running" value={totals.running.toLocaleString()} />
        <Stat label="Failed" value={totals.failed.toLocaleString()} tone={totals.failed > 0 ? "danger" : "default"} />
        <Stat label="AI calls (1h)" value={(aiRuns?.length ?? 0).toLocaleString()} sub={`${inflightCount} in-flight`} />
        <Stat label="AI spend (1h)" value={`$${inflightCost.toFixed(2)}`} tone={inflightCost > 5 ? "warn" : "default"} />
      </div>

      {stuck.length > 0 && (
        <Card className="border-amber-500/40 bg-amber-50/30 dark:bg-amber-950/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <h3 className="text-sm font-semibold">Stuck jobs ({stuck.length})</h3>
              <span className="text-xs text-muted-foreground">Running but no update for &gt;30 min</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {stuck.slice(0, 12).map((s) => (
                <Badge key={s.id} variant="outline" className="text-xs">
                  {s.job_type} · last update {fmt(s.updated_at)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {sumLoading ? (
            <p className="text-sm text-muted-foreground p-4">Loading…</p>
          ) : grouped.length === 0 ? (
            <p className="text-sm text-muted-foreground p-4">No queued, running, or failed jobs.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job type</TableHead>
                  <TableHead className="text-right">Queued</TableHead>
                  <TableHead className="text-right">Running</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grouped.map((g) => (
                  <TableRow key={g.job_type}>
                    <TableCell className="font-mono text-xs">{g.job_type}</TableCell>
                    <TableCell className="text-right">{(g.queued ?? 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right">{(g.running ?? 0).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      {(g.failed ?? 0) > 0 ? (
                        <span className="text-destructive font-medium">{g.failed}</span>
                      ) : (
                        g.failed ?? 0
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {(g.queued ?? 0) > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (confirm(`Cancel all ${g.queued} queued ${g.job_type} jobs?`)) cancelGroup.mutate(g.job_type);
                          }}
                          disabled={cancelGroup.isPending}
                        >
                          <Ban className="h-3 w-3 mr-1" /> Cancel queue
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2">
        {(["active", "failed", "all"] as const).map((f) => (
          <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)}>
            {f === "active" ? "Active" : f === "failed" ? "Failed" : "All recent"}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground p-4">Loading…</p>
          ) : (rows ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground p-4">No matching jobs.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Attempts</TableHead>
                  <TableHead>Next run</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rows ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.job_type}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`border-0 ${STATUS_STYLE[r.status] ?? ""}`}>
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {r.attempts}/{r.max_attempts}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{fmt(r.next_run_at)}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{fmt(r.updated_at)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate" title={r.last_error ?? ""}>
                      {r.last_error ?? "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.status === "failed" && (
                        <Button size="sm" variant="ghost" onClick={() => retryFailed.mutate(r.id)}>
                          Retry
                        </Button>
                      )}
                      {(r.status === "queued" || r.status === "running") && (
                        <Button size="sm" variant="ghost" onClick={() => cancelOne.mutate(r.id)}>
                          Cancel
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {(aiRuns?.length ?? 0) > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-3">Recent AI calls (last hour)</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Latency</TableHead>
                  <TableHead>Started</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(aiRuns ?? []).slice(0, 20).map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">{r.action}</TableCell>
                    <TableCell className="font-mono text-xs">{r.model}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {!r.completed_at ? "in-flight" : r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-xs">${Number(r.cost_usd ?? 0).toFixed(4)}</TableCell>
                    <TableCell className="text-right text-xs">
                      {r.latency_ms ? `${r.latency_ms}ms` : "—"}
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{fmt(r.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, sub, tone = "default" }: { label: string; value: string; sub?: string; tone?: "default" | "warn" | "danger" }) {
  const toneClass =
    tone === "danger" ? "text-destructive" : tone === "warn" ? "text-amber-600 dark:text-amber-400" : "";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
        <div className={`text-2xl font-semibold mt-1 ${toneClass}`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}
